import React, { FC, useState } from "react";

import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { getGetUserTipsQueryKey, useDeleteUserTips } from "@api";

import { Modal } from "@ui/components";
import { Button, Text } from "@ui/elements";
import { Stack } from "@ui/layout";

import { toaster } from "@components";

type DeleteConfirmationModalProps = {
  onClose: () => void;
  open: boolean;
};

const DeleteConfirmationModal: FC<DeleteConfirmationModalProps> = ({
  onClose,
  open,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const {
    mutateAsync: deleteTips,
    isPending: deleteTipsLoading,
    error: deleteTipsError,
  } = useDeleteUserTips();

  const handleDeleteTips = async () => {
    await deleteTips();

    toaster({
      title: t("messages.successful-update"),
      message: t("messages.delete-portfolio-success"),
    });

    onClose();

    void queryClient.invalidateQueries({
      queryKey: getGetUserTipsQueryKey({
        type: "resultOriented",
      }),
    });
    void queryClient.invalidateQueries({
      queryKey: getGetUserTipsQueryKey({
        type: "value",
      }),
    });
    void queryClient.invalidateQueries({
      queryKey: getGetUserTipsQueryKey({
        type: "value-v2",
      }),
    });
  };

  return (
    <Modal
      isActive={open}
      onClose={onClose}
      header={t("messages.are-you-sure")}
      style={{ zIndex: 100 }}
    >
      <Stack direction="column" gap="xl">
        <Stack direction="column" gap="xl">
          <Stack direction="column">
            <Text>{t("messages.delete-portfolio-confirmation")}</Text>
          </Stack>
        </Stack>
        {deleteTipsError && (
          <div className="notification is-danger is-light is-size-7">
            {deleteTipsError?.response?.data.error}
          </div>
        )}
        <Stack justify="end">
          <Button variant="light" onClick={onClose}>
            {t("labels.cancel")}
          </Button>
          <Button
            variant="light"
            color="danger"
            onClick={() => handleDeleteTips()}
            isLoading={deleteTipsLoading}
          >
            {t("labels.im-sure")}
          </Button>
        </Stack>
      </Stack>
    </Modal>
  );
};

export const MyBets = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <Stack direction="column" gap="xl">
      <DeleteConfirmationModal open={open} onClose={() => setOpen(false)} />
      <Text variant="secondary">{t("messages.reset-portfolio-info")}</Text>
      <Stack justify="end">
        <Button color="danger" variant="light" onClick={() => setOpen(true)}>
          {t("labels.reset-my-bets")}
        </Button>
      </Stack>
    </Stack>
  );
};
